import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import toast from 'react-hot-toast';
import i18n from '@/lib/i18n';
import { authHelpers } from '@/stores/authStore';
import { apiClient } from '@/lib/api';
import type { UserRole } from '@/types';

// Re-exported: EmployeesPage imports UserRole from this module, which only
// declared it locally.
export type { UserRole };

/*
 * Staff, from /api/v1/users.
 *
 * This store used to keep the whole roster in localStorage behind
 * `setTimeout(resolve, 1000)` calls dressed up as network requests. Adding an
 * employee showed a success toast and pushed a record with an `emp-<timestamp>`
 * id and a hardcoded `restaurantId: 'nova-cafe-1'`. That person could not sign
 * in, did not exist to any other screen, and vanished on another device or in a
 * different browser — for a screen whose entire purpose is onboarding staff.
 *
 * POST /users has always worked; the operational-flow e2e suite hires through
 * it. Nothing here is new backend, only wiring.
 *
 * Fields the API does not store are gone rather than collected and dropped:
 * there is no department and no free-text notes on a user, and "hire date" is
 * the account's own createdAt, so it is shown but not editable.
 */

export interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  /** `name@restaurantCode` — the whole login, for display. */
  login: string;
  /** The short half: what the owner typed, and the only half they can edit. */
  loginName: string;
  /** The way this person is actually reached — WhatsApp, by hand. */
  phone?: string;
  role: UserRole;
  isActive: boolean;
  /** Account creation — the closest thing the model has to a hire date. */
  hireDate: Date;
  salary?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface EmployeeFormData {
  firstName: string;
  lastName: string;
  /**
   * The short name only — `aigul`. The `@restaurantCode` suffix is appended by
   * the server from the owner's own restaurant, which is what lets two cafes
   * each employ an `aigul`.
   */
  name: string;
  /** Required: with no email channel, a phone number is the only way to reach staff. */
  phone: string;
  role: UserRole;
  /** Required when hiring; the API has no password generation for staff. */
  password?: string;
  salary?: number;
  isActive: boolean;
}

interface EmployeeState {
  employees: Employee[];
  isLoading: boolean;
  hasLoaded: boolean;
  error: string | null;
}

interface EmployeeActions {
  loadEmployees: () => Promise<void>;
  addEmployee: (employeeData: EmployeeFormData) => Promise<void>;
  updateEmployee: (id: string, employeeData: Partial<EmployeeFormData>) => Promise<void>;
  deleteEmployee: (id: string) => Promise<void>;
  getEmployee: (id: string) => Employee | undefined;

  getActiveEmployees: () => Employee[];
  getEmployeesByRole: (role: UserRole) => Employee[];
  searchEmployees: (query: string) => Employee[];

  getEmployeeStats: () => {
    total: number;
    active: number;
    newThisMonth: number;
    byRole: Record<UserRole, number>;
  };

  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

type EmployeeStore = EmployeeState & EmployeeActions;

/** Salary is `{ type, amount, currency }` on the model, a plain number here. */
const salaryAmount = (salary: unknown): number | undefined => {
  if (typeof salary === 'number') return salary;
  if (salary && typeof salary === 'object') {
    const amount = (salary as { amount?: unknown }).amount;
    if (typeof amount === 'number') return amount;
  }
  return undefined;
};

const mapUser = (raw: any): Employee => {
  const created = raw.createdAt ? new Date(raw.createdAt) : new Date();
  return {
    id: raw._id ?? raw.id,
    firstName: raw.firstName ?? '',
    lastName: raw.lastName ?? '',
    name: `${raw.firstName ?? ''} ${raw.lastName ?? ''}`.trim(),
    login: raw.login ?? '',
    loginName: raw.loginName ?? String(raw.login ?? '').split('@')[0] ?? '',
    phone: raw.phone,
    role: raw.role,
    isActive: raw.isActive ?? true,
    hireDate: created,
    salary: salaryAmount(raw.salary),
    createdAt: created,
    updatedAt: raw.updatedAt ? new Date(raw.updatedAt) : created,
  };
};

/** The server's message is more useful than a generic one; fall back if absent. */
const apiMessage = (error: any, fallbackKey: string): string => {
  const fieldErrors = error?.response?.data?.errors;
  if (Array.isArray(fieldErrors) && fieldErrors.length) {
    return fieldErrors.map((e: any) => e.msg ?? e.message).filter(Boolean).join('; ');
  }
  return error?.response?.data?.message || error?.message || i18n.t(fallbackKey);
};

export const useEmployeeStore = create<EmployeeStore>()(
  immer((set, get) => ({
    employees: [],
    isLoading: false,
    hasLoaded: false,
    error: null,

    loadEmployees: async () => {
      set(draft => {
        draft.isLoading = true;
        draft.error = null;
      });

      try {
        // The roster is small and the list is a single screen, so page through
        // it rather than showing a partial team.
        const limit = 50;
        const collected: any[] = [];
        let page = 1;
        let totalPages = 1;

        do {
          const response = await apiClient.get('/users', { params: { page, limit } });
          const data: any = response.data ?? {};
          collected.push(...(data.users ?? []));
          totalPages = data.pagination?.totalPages ?? 1;
          page += 1;
        } while (page <= totalPages && page <= 10);

        set(draft => {
          draft.employees = collected.map(mapUser);
          draft.isLoading = false;
          draft.hasLoaded = true;
        });
      } catch (error: any) {
        set(draft => {
          draft.error = apiMessage(error, 'employees.toast.loadFailed');
          draft.isLoading = false;
          draft.hasLoaded = true;
        });
      }
    },

    addEmployee: async (employeeData: EmployeeFormData) => {
      set(draft => {
        draft.isLoading = true;
        draft.error = null;
      });

      try {
        // restaurantId is deliberately absent: the API takes it from the
        // authenticated owner's token, and ignores it in the body anyway.
        // `name`, not a login: the API refuses to take the suffix from a
        // request body, so an owner can only ever create staff inside their
        // own restaurant.
        await apiClient.post('/users', {
          name: employeeData.name,
          password: employeeData.password,
          firstName: employeeData.firstName,
          lastName: employeeData.lastName,
          phone: employeeData.phone,
          role: employeeData.role,
          ...(employeeData.salary
            ? { salary: { type: 'monthly', amount: employeeData.salary, currency: 'KGS' } }
            : {}),
        });

        await get().loadEmployees();

        toast.success(i18n.t('employees.employeeAdded'), { duration: 3000, icon: '👥' });
      } catch (error: any) {
        const message = apiMessage(error, 'employees.toast.addFailed');
        set(draft => {
          draft.error = message;
          draft.isLoading = false;
        });
        toast.error(message);
        // Rethrown so the form stays open on failure instead of closing over a
        // record that was never created.
        throw error;
      }
    },

    updateEmployee: async (id: string, employeeData: Partial<EmployeeFormData>) => {
      set(draft => {
        draft.isLoading = true;
        draft.error = null;
      });

      try {
        const updates: Record<string, unknown> = {};
        if (employeeData.firstName !== undefined) updates.firstName = employeeData.firstName;
        if (employeeData.lastName !== undefined) updates.lastName = employeeData.lastName;
        if (employeeData.name !== undefined) updates.name = employeeData.name;
        if (employeeData.phone !== undefined) updates.phone = employeeData.phone;
        if (employeeData.role !== undefined) updates.role = employeeData.role;
        if (employeeData.isActive !== undefined) updates.isActive = employeeData.isActive;
        if (employeeData.salary !== undefined) {
          updates.salary = { type: 'monthly', amount: employeeData.salary, currency: 'KGS' };
        }

        await apiClient.put(`/users/${id}`, updates);
        await get().loadEmployees();

        toast.success(i18n.t('employees.employeeUpdated'), { duration: 3000, icon: '✏️' });
      } catch (error: any) {
        const message = apiMessage(error, 'employees.toast.updateFailed');
        set(draft => {
          draft.error = message;
          draft.isLoading = false;
        });
        toast.error(message);
        throw error;
      }
    },

    deleteEmployee: async (id: string) => {
      set(draft => {
        draft.isLoading = true;
        draft.error = null;
      });

      try {
        // The API deactivates rather than destroying: the person keeps their
        // order history, and the card shows them as inactive.
        await apiClient.delete(`/users/${id}`);
        await get().loadEmployees();

        toast.success(i18n.t('employees.employeeDeactivated'), { duration: 3000, icon: '🚫' });
      } catch (error: any) {
        const message = apiMessage(error, 'employees.toast.deleteFailed');
        set(draft => {
          draft.error = message;
          draft.isLoading = false;
        });
        toast.error(message);
      }
    },

    getEmployee: (id: string) => get().employees.find(emp => emp.id === id),

    getActiveEmployees: () => get().employees.filter(emp => emp.isActive),

    getEmployeesByRole: (role: UserRole) =>
      get().employees.filter(emp => emp.role === role && emp.isActive),

    searchEmployees: (query: string) => {
      const searchTerm = query.toLowerCase();
      return get().employees.filter(
        emp =>
          emp.firstName.toLowerCase().includes(searchTerm) ||
          emp.lastName.toLowerCase().includes(searchTerm) ||
          emp.login.toLowerCase().includes(searchTerm) ||
          (emp.phone ?? '').includes(searchTerm) ||
          emp.role.toLowerCase().includes(searchTerm)
      );
    },

    getEmployeeStats: () => {
      const employees = get().employees;
      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const byRole = employees.reduce((acc, emp) => {
        if (emp.isActive) {
          acc[emp.role] = (acc[emp.role] || 0) + 1;
        }
        return acc;
      }, {} as Record<UserRole, number>);

      return {
        total: employees.length,
        active: employees.filter(emp => emp.isActive).length,
        newThisMonth: employees.filter(emp => emp.hireDate >= thisMonth && emp.isActive).length,
        byRole,
      };
    },

    setLoading: (loading: boolean) => {
      set(draft => {
        draft.isLoading = loading;
      });
    },

    setError: (error: string | null) => {
      set(draft => {
        draft.error = error;
      });
    },
  }))
);

// Employee helper functions
export const employeeHelpers = {
  formatSalary: (salary?: number): string => {
    if (!salary) return i18n.t('common.notSpecified', 'Не указано');
    return new Intl.NumberFormat('ru-KG', {
      style: 'currency',
      currency: 'KGS',
      minimumFractionDigits: 0
    }).format(salary);
  },

  formatHireDate: (date: Date): string => {
    return new Intl.DateTimeFormat('ru-KG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Asia/Bishkek'
    }).format(new Date(date));
  },

  getEmployeeDisplayName: (employee: Employee): string => {
    return `${employee.firstName} ${employee.lastName}`;
  },

  // Delegates: role labels have one source, in authHelpers. This used to read
  // its own `employees.roles.*` namespace, which is why the staff list and the
  // sidebar disagreed about what to call a manager, a chef and a waiter — and
  // why a super admin on a staff list rendered as the literal `super_admin`.
  getRoleDisplayName: (role: UserRole): string => authHelpers.getRoleDisplayName(role),

  getRoleBadgeColor: (role: UserRole): string => {
    const colors = {
      restaurant_owner: 'bg-red-100 text-red-800',
      manager: 'bg-blue-100 text-blue-800',
      chef: 'bg-green-100 text-green-800',
      waiter: 'bg-purple-100 text-purple-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  },

  getStatusBadgeColor: (isActive: boolean): string => {
    return isActive
      ? 'bg-green-100 text-green-800'
      : 'bg-gray-100 text-gray-800';
  }
};
